import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from 'radix-ui';
import { Loader2 } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';
import { Icon, type LucideIcon } from './icon';
import { Kbd } from './kbd';
import { Tooltip } from './tooltip';

export const buttonVariants = cva(
  'inline-flex shrink-0 cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-full border border-transparent font-sans font-medium no-underline transition-[background-color,border-color,color,opacity] duration-150 ease-out disabled:pointer-events-none disabled:opacity-45',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:opacity-88',
        secondary: 'border-border bg-secondary text-secondary-foreground hover:bg-accent',
        ghost: 'bg-transparent text-foreground hover:bg-accent',
        danger: 'bg-destructive text-destructive-foreground hover:opacity-88',
      },
      size: {
        sm: 'h-control-sm gap-1.5 px-[11px] text-[13px] leading-[18px]',
        md: 'h-control-md px-3.5 text-sm',
        lg: 'h-control-lg px-[18px] text-sm',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Lucide-Icon vor dem Text. */
  icon?: LucideIcon;
  /** Lucide-Icon nach dem Text. */
  iconRight?: LucideIcon;
  /** Tastenkürzel am Ende, z. B. „N“. */
  kbd?: string;
  /** Rendert das Kind (z. B. einen Link) mit Button-Optik. */
  asChild?: boolean;
  /** Läuft: Spinner statt Icon, gesperrt, aria-busy. Für Speichern und Senden. */
  loading?: boolean;
}

/**
 * Pill-Button. `secondary` ist der Standard, `primary` höchstens einmal pro Ansicht,
 * `danger` immer mit Bestätigung. Text: Verb zuerst, Infinitiv („Angebot senden“).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, icon, iconRight, kbd, asChild, loading, disabled, className, children, type = 'button', ...rest },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button';
  const is = size === 'sm' ? 14 : 16;
  const kbdAufFarbe = variant === 'primary' || variant === 'danger';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size }), className)}
      {...rest}
    >
      {loading ? <Loader2 size={is} strokeWidth={1.75} aria-hidden className="shrink-0 animate-spin" /> : icon && <Icon icon={icon} size={is} />}
      <Slot.Slottable>{children}</Slot.Slottable>
      {iconRight && <Icon icon={iconRight} size={is} />}
      {kbd && <Kbd className={cn(kbdAufFarbe && 'border-current bg-transparent text-inherit')}>{kbd}</Kbd>}
    </Comp>
  );
});

export const iconButtonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center border p-0 transition-colors duration-150 ease-out disabled:pointer-events-none disabled:opacity-45 aria-pressed:border-transparent aria-pressed:bg-highlight-soft aria-pressed:text-highlight',
  {
    variants: {
      variant: {
        outline: 'border-border bg-card text-foreground hover:bg-accent',
        ghost: 'border-transparent bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground',
      },
      size: {
        sm: 'size-control-sm rounded-sm',
        md: 'size-control-md rounded-md',
      },
    },
    defaultVariants: { variant: 'outline', size: 'md' },
  },
);

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof iconButtonVariants> {
  icon: LucideIcon;
  /** Pflicht: wird aria-label und Tooltip. */
  label: string;
  /** Für Umschalter: gedrückt in highlight-soft. */
  pressed?: boolean;
  /** Tooltip abschalten, etwa wenn ein Menü darüber aufgeht. */
  noTooltip?: boolean;
}

/** Quadratischer Button mit genau einem Icon, für Toolbars und Zeilenaktionen. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, variant, size, pressed, noTooltip, className, type = 'button', ...rest },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      aria-pressed={pressed}
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...rest}
    >
      <Icon icon={icon} size={size === 'sm' ? 15 : 17} />
    </button>
  );
  return noTooltip ? button : <Tooltip content={label}>{button}</Tooltip>;
});
